<script setup lang="ts">
import type { RegistrationUpdateByBiometryForm } from '~/store-v2/registration/useRegistrationContactChange'
import { cellphone } from '@solfacil/frontend-utils'
import { SolButton, SolInputText } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { getSchema } from './biometry.schema'

const props = defineProps<{
  isOpen: boolean
  registration: any
  projectId: string
  isPending: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submitEdit', payload: RegistrationUpdateByBiometryForm): void
}>()

const { t } = useI18n()

const remainingChanges = computed(() => props.registration?.remaining_changes_after_confirmation ?? 0)

const validationSchema = computed(() => toTypedSchema(getSchema(t, props.registration)!))
const updateForm = useForm({ validationSchema: validationSchema.value, keepValuesOnUnmount: false })

const legals = computed(() => props.registration?.company?.legal_representatives?.filter((legal: any) => legal.cpf !== props.registration?.company?.majority_partner.cpf))
const secondLegalRepresentant = computed(() => legals.value && legals.value[1])
const hasFirstLegalRepresentant = computed(() => legals.value && !!legals.value[0]?.cpf)
const hasSecondLegalRepresentant = computed(() => legals.value && !!legals.value[1]?.cpf)

const [biometryEmailModel, biometryEmailProps] = updateForm.defineField('email')
const [biometryPhoneModel, biometryPhoneProps] = updateForm.defineField('phone')
const [biometryEmailGuarantorModel, biometryEmailGuarantorProps] = updateForm.defineField('emailGuarantor')
const [biometryPhoneGuarantorModel, biometryPhoneGuarantorProps] = updateForm.defineField('phoneGuarantor')
const [majorityPartnerEmailModel, majorityPartnerEmailProps] = updateForm.defineField('majority_partner_person_form_email')
const [majorityPartnerPhoneModel, majorityPartnerPhoneProps] = updateForm.defineField('majority_partner_person_form_phone')
const [firstLegalRepresentantEmailModel, firstLegalRepresentantEmailProps] = updateForm.defineField('first_legal_representant_email')
const [firstLegalRepresentantPhoneModel, firstLegalRepresentantPhoneProps] = updateForm.defineField('first_legal_representant_phone')
const [secondLegalRepresentantEmailModel, secondLegalRepresentantEmailProps] = updateForm.defineField('second_legal_representant_email')
const [secondLegalRepresentantPhoneModel, secondLegalRepresentantPhoneProps] = updateForm.defineField('second_legal_representant_phone')

function phoneForBiometryForm(phone?: string | null) {
  const n = cellphone.normalize(phone ?? '')
  return n ? cellphone.applyMask(n) : ''
}

function populate() {
  if (props.registration?.person_type === 'pf') {
    updateForm.setValues({
      email: props.registration?.customer?.contact?.email,
      phone: phoneForBiometryForm(props.registration?.customer?.contact?.phone),
      emailGuarantor: props.registration?.guarantor?.contact?.email,
      phoneGuarantor: phoneForBiometryForm(props.registration?.guarantor?.contact?.phone),
    }, true)
  }
  else {
    const legalsList = props.registration?.company?.legal_representatives?.filter((legal: any) => legal.cpf !== props.registration?.company?.majority_partner.cpf)
    updateForm.setValues({
      majority_partner_person_form_email: props.registration?.company?.majority_partner?.contact?.email,
      majority_partner_person_form_phone: phoneForBiometryForm(props.registration?.company?.majority_partner?.contact?.phone),
    }, true)
    if (legalsList && legalsList[0]) {
      updateForm.setValues({
        first_legal_representant_email: legalsList[0]?.contact?.email,
        first_legal_representant_phone: phoneForBiometryForm(legalsList[0]?.contact?.phone),
      }, true)
    }
    if (legalsList && legalsList[1]) {
      updateForm.setValues({
        second_legal_representant_email: legalsList[1]?.contact?.email,
        second_legal_representant_phone: phoneForBiometryForm(legalsList[1]?.contact?.phone),
      }, true)
    }
  }
}

watch(() => props.isOpen, (open) => {
  if (open)
    populate()
})

async function createCompanyPayload(): Promise<RegistrationUpdateByBiometryForm> {
  const { values } = await updateForm.validate()
  if (!values || !('majority_partner_person_form_phone' in values))
    throw new Error('Invalid values')

  const majorityPartnerIsLegalRepresentative = props.registration?.company?.majority_partner_is_legal_representative
  const hasFirstLegal = !!props.registration?.company?.legal_representatives[0] && !majorityPartnerIsLegalRepresentative
  const hasSecondLegal = !!props.registration?.company?.legal_representatives[1]

  return {
    project_id: props.projectId,
    majority_partner: { email: values.majority_partner_person_form_email!, phone: values.majority_partner_person_form_phone! },
    legal_representative_1: hasFirstLegal ? { email: values.first_legal_representant_email!, phone: values.first_legal_representant_phone! } : null,
    legal_representative_2: hasSecondLegal ? { email: values.second_legal_representant_email!, phone: values.second_legal_representant_phone! } : null,
  }
}

async function createCustomerPayload(): Promise<RegistrationUpdateByBiometryForm> {
  const { values } = await updateForm.validate()
  if (!values || !('phone' in values))
    throw new Error('Invalid values')

  const hasGuarantor = !!props.registration?.guarantor
  return {
    project_id: props.projectId,
    customer: { email: values.email!, phone: values.phone! },
    guarantor: hasGuarantor ? { email: values.emailGuarantor!, phone: values.phoneGuarantor! } : null,
  }
}

async function handleSave() {
  const { valid } = await updateForm.validate()
  if (!valid)
    return

  const payload = props.registration?.person_type === 'pf'
    ? await createCustomerPayload()
    : await createCompanyPayload()

  emit('submitEdit', payload)
}
</script>

<template>
  <SolModal
    id="biometry-contact-edit"
    :is-open="isOpen"
    :title="t('customer.biometry.edit_modal.title')"
    :size="{ desktop: 'medium', mobile: 'bottom-sheet' }"
    @close="emit('close')"
  >
    <SolAlert id="edit-analysis-warning" feedback="informative" class="mb-6">
      <span class="fonts-body-small-regular text-neutral-low-medium">{{ t('customer.biometry.edit_modal.warning_before') }} <strong>{{ t('customer.biometry.edit_modal.warning_times', { count: remainingChanges }) }}</strong>. {{ t('customer.biometry.edit_modal.warning_after') }}</span>
    </SolAlert>

    <form v-if="registration?.person_type === 'pf'" class="grid grid-cols-1 sm:system:grid-cols-2 gap-4">
      <SolInputText
        id="email" v-model="biometryEmailModel" name="email" v-bind="biometryEmailProps"
        :label="`${t('customer.biometry.edit_modal.email_label')}*`" :error="updateForm.errors.value.email"
      />
      <SolInputText
        id="phone" v-model="biometryPhoneModel" name="phone" v-bind="biometryPhoneProps" :mask="cellphone.mask"
        :label="`${t('customer.biometry.edit_modal.phone_label')}*`" :error="updateForm.errors.value.phone"
      />
      <SolInputText
        v-if="registration?.guarantor"
        id="email-guarantor" v-model="biometryEmailGuarantorModel" name="emailGuarantor" v-bind="biometryEmailGuarantorProps"
        :label="t('customer.biometry.modal_sending.form.email_guarantor')" :error="updateForm.errors.value.emailGuarantor"
      />
      <SolInputText
        v-if="registration?.guarantor"
        id="phone-guarantor" v-model="biometryPhoneGuarantorModel" name="phoneGuarantor" v-bind="biometryPhoneGuarantorProps" :mask="cellphone.mask"
        :label="t('customer.biometry.modal_sending.form.phone_guarantor')" :error="updateForm.errors.value.phoneGuarantor"
      />
    </form>

    <form v-else class="grid grid-cols-1 sm:system:grid-cols-2 gap-4">
      <SolInputText
        id="majority_partner_person_form_email" v-model="majorityPartnerEmailModel" name="majority_partner_person_form_email" v-bind="majorityPartnerEmailProps"
        :label="t('company.biometry.modal_sending.form.majority.email')" :error="updateForm.errors.value.majority_partner_person_form_email"
      />
      <SolInputText
        id="majority_partner_person_form_phone" v-model="majorityPartnerPhoneModel" name="majority_partner_person_form_phone" v-bind="majorityPartnerPhoneProps" :mask="cellphone.mask"
        :label="t('company.biometry.modal_sending.form.majority.phone')" :error="updateForm.errors.value.majority_partner_person_form_phone"
      />
      <SolInputText
        v-if="hasFirstLegalRepresentant"
        id="first_legal_representant_email" v-model="firstLegalRepresentantEmailModel" name="first_legal_representant_email" v-bind="firstLegalRepresentantEmailProps"
        :label="secondLegalRepresentant ? t('company.biometry.modal_sending.form.legal_1.email') : t('company.biometry.modal_sending.form.legal.email')" :error="updateForm.errors.value.first_legal_representant_email"
      />
      <SolInputText
        v-if="hasFirstLegalRepresentant"
        id="first_legal_representant_phone" v-model="firstLegalRepresentantPhoneModel" name="first_legal_representant_phone" v-bind="firstLegalRepresentantPhoneProps" :mask="cellphone.mask"
        :label="secondLegalRepresentant ? t('company.biometry.modal_sending.form.legal_1.phone') : t('company.biometry.modal_sending.form.legal.phone')" :error="updateForm.errors.value.first_legal_representant_phone"
      />
      <SolInputText
        v-if="hasSecondLegalRepresentant"
        id="second_legal_representant_email" v-model="secondLegalRepresentantEmailModel" name="second_legal_representant_email" v-bind="secondLegalRepresentantEmailProps"
        :label="t('company.biometry.modal_sending.form.legal_2.email')" :error="updateForm.errors.value.second_legal_representant_email"
      />
      <SolInputText
        v-if="hasSecondLegalRepresentant"
        id="second_legal_representant_phone" v-model="secondLegalRepresentantPhoneModel" name="second_legal_representant_phone" v-bind="secondLegalRepresentantPhoneProps" :mask="cellphone.mask"
        :label="t('company.biometry.modal_sending.form.legal_2.phone')" :error="updateForm.errors.value.second_legal_representant_phone"
      />
    </form>

    <template #action-secondary>
      <SolButton id="cancel_edit_data" size="medium" variant="secondary" :disabled="isPending" @click="emit('close')">
        {{ t('customer.biometry.edit_modal.cancel') }}
      </SolButton>
    </template>
    <template #action-primary>
      <SolButton id="save_edit_data" size="medium" :loading="isPending" @click="handleSave">
        {{ t('customer.biometry.edit_modal.save') }}
      </SolButton>
    </template>
  </SolModal>
</template>
